import { t } from "@lingui/macro";
import { useIsFocused } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { Container } from "@whoosh/design-system/src/apps/PatronApp/layout/Container/Container";
import { CardScroller } from "@whoosh/design-system/src/components/display";
import { Text } from "@whoosh/design-system/src/components/primitive";

import { BookingCard } from "../../../../features/BookingCard";
import { compareDateTime } from "../../../../utils/sort";
import { MemberSchedule_Bookings$key } from "./__generated__/MemberSchedule_Bookings.graphql";
import { MemberSchedule_EventSignUps$key } from "./__generated__/MemberSchedule_EventSignUps.graphql";
import { EventSignUpCardWrapper } from "./components/EventSignUpCardWrapper";
import { MyScheduleItem } from "./types";

const getDateTimeParts = (dateTime: string | null | undefined) => {
  const [date = "", rawTime = ""] = dateTime?.split(/[T ]/) ?? [];
  const time = rawTime
    .replace(/\.\d+Z?$/, "")
    .replace(/Z$/, "")
    .slice(0, 8);

  return { date, time };
};

type Props = {
  memberBookingsRef: MemberSchedule_Bookings$key | null;
  memberEventSignUpsRef: MemberSchedule_EventSignUps$key | null;
};

export const MemberSchedule = ({
  memberBookingsRef,
  memberEventSignUpsRef,
}: Props) => {
  const memberBookings = useFragment(
    MemberScheduleBookingsFragmentString,
    memberBookingsRef
  );

  const memberEventSignUps = useFragment(
    MemberScheduleEventSignUpsFragmentString,
    memberEventSignUpsRef
  );

  // this component sometimes tries to update itself when not focused
  // resulting in errors of multiple components trying to update at the same time
  // this stops that
  const isFocused = useIsFocused();
  if (!isFocused) return null;

  // we show only standard bookings, which are not event session bookings (this is why we checked for eventRegistrations count = 0)
  const allBookings = [
    ...(memberBookings?.bookings?.edges?.filter(
      (booking) => (booking?.node?.eventRegistrations?.count ?? 0) === 0
    ) || []),
    ...(memberBookings?.managedBookings?.edges?.filter(
      (booking) => (booking?.node?.eventRegistrations?.count ?? 0) === 0
    ) || []),
  ];

  const bookingsList: MyScheduleItem[] =
    allBookings?.map((booking) => {
      const startSlotDateTime = getDateTimeParts(
        booking?.node?.startSlot?.dateTime
      );
      const firstAgendaDateTime = getDateTimeParts(
        booking?.node?.startSlot?.agenda?.timeSlots?.firstDateTime
      );

      return {
        type: "Booking",
        id: booking?.node?.id || "",
        date:
          startSlotDateTime.date ||
          booking?.node?.startSlot?.agenda.date ||
          firstAgendaDateTime.date ||
          "",
        time:
          startSlotDateTime.time ||
          booking?.node?.startSlot?.time ||
          firstAgendaDateTime.time ||
          "",
        ref: booking?.node ?? null,
      };
    }) || [];

  const eventSignUps: MyScheduleItem[] =
    memberEventSignUps?.eventSignUps?.edges?.map((eventSignUp) => {
      return {
        type: "EventSignUp",
        id: eventSignUp?.node?.id || "",
        date: eventSignUp?.node?.event?.startOn || "",
        time:
          eventSignUp?.node?.event?.eventSessions?.edges?.[0]?.node?.startAt ||
          "",
        ref: eventSignUp?.node ?? null,
      };
    }) || [];

  const myScheduleItems: MyScheduleItem[] = [
    ...(bookingsList || []),
    ...(eventSignUps || []),
  ];

  const sortedBookingAndEventRegistrations =
    myScheduleItems.sort(compareDateTime);

  return (
    <Container pb={4} paddingHorizontal={{ base: 0, lg: 12 }}>
      <CardScroller
        data={sortedBookingAndEventRegistrations}
        paddingHorizontal={{ base: 6, md: 16, lg: 0 }}
        titleElement={
          <Text
            variant={{
              base: "mainHeading",
              lg: "mainTitle2",
            }}
            mb={{ base: 0, lg: 3 }}
            px={{ base: 6, md: 16, lg: 0 }}
          >
            {t`Your Schedule`}
          </Text>
        }
        renderItem={(item) =>
          item.type === "EventSignUp" ? (
            item?.ref ? (
              <EventSignUpCardWrapper
                eventSignUpCardWrapperRef={item.ref ?? null}
              />
            ) : null
          ) : item?.ref ? (
            <BookingCard BookingCardRef={item.ref ?? null} />
          ) : null
        }
      />
    </Container>
  );
};

export const MemberScheduleBookingsFragmentString = graphql`
  fragment MemberSchedule_Bookings on Member {
    id
    club {
      departments {
        count
      }
    }
    bookings(
      dateTimeGte: $dateTimeGTE
      first: 100
      orderBy: { field: DATE_TIME, direction: ASC }
    ) {
      count
      edges {
        node {
          ... on StandardBooking {
            eventRegistrations {
              count
            }
          }
          __typename
          startSlot {
            agenda {
              date
              timeSlots {
                firstDateTime
              }
            }
            ... on TimeSlot {
              dateTime
              time
            }
          }
          id
          ...BookingCard_Booking
        }
      }
    }
    managedBookings(
      dateTimeGte: $dateTimeGTE
      first: 100
      orderBy: { field: DATE_TIME, direction: ASC }
    ) {
      count
      edges {
        node {
          ... on StandardBooking {
            eventRegistrations {
              count
            }
          }
          __typename
          startSlot {
            agenda {
              date
              timeSlots {
                firstDateTime
              }
            }
            ... on TimeSlot {
              dateTime
              time
            }
          }
          id
          ...BookingCard_Booking
        }
      }
    }
  }
`;

export const MemberScheduleEventSignUpsFragmentString = graphql`
  fragment MemberSchedule_EventSignUps on Member {
    id
    club {
      departments {
        count
      }
    }
    eventSignUps(dateGte: $dateGTE, first: 100) {
      count
      edges {
        node {
          ...EventSignUpCardWrapper_EventSignUp

          id
          __typename
          event {
            startOn
            eventSessions {
              edges {
                node {
                  startAt
                }
              }
            }
          }
        }
      }
    }
  }
`;
